import { useState } from 'react';
import C from '../../constants/palette';
import StoneVisual from '../shared/StoneVisual';
import StatusBadge from '../shared/StatusBadge';
import { displayImageUrl } from '../../utils/images';

export default function StoneDetailPage({ stone, onReserve, onBack }) {
  const [activeImg, setActiveImg] = useState(0);

  const imageSrc = displayImageUrl(stone);
  const additionalImages = (stone.additionalImages || []).map(displayImageUrl).filter(Boolean);
  const hasAdditionalImages = additionalImages.length > 0;
  const gallery = hasAdditionalImages ? [imageSrc, ...additionalImages] : [imageSrc, imageSrc, imageSrc, imageSrc];
  // Stable, deliberately varied angles make the fallback tiles read as a
  // curated stack while remaining identical on every reload.
  const fallbackTileAngles = [-2.5, 1.5, -1.25, 2.25];

  const specs = [
    { label: 'Treasure ID', value: stone.id },
    { label: 'Author',      value: stone.author || 'Unknown' },
    { label: 'Valuation',   value: `$${stone.price.toLocaleString()}` },
    { label: 'Status',      value: stone.status },
    { label: 'Collection',  value: 'Kingstrade archive' },
  ];

  const imgFilter = (i) => (
    i === 1 ? 'saturate(0.6)' :
    i === 2 ? 'brightness(1.12)' :
    i === 3 ? 'contrast(1.15)' : 'none'
  );

  const thumbFilter = (i) => (
    i === 1 ? 'saturate(0.5)' :
    i === 2 ? 'brightness(1.15)' :
    i === 3 ? 'contrast(1.2) hue-rotate(10deg)' : 'none'
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>

      {/* ── Header ── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: '#F8F9FAf8', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.border}`,
        padding: '0 48px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontFamily: 'Playfair Display', fontSize: 22, fontWeight: 500, color: C.ink, letterSpacing: '0.04em' }}>
            KINGSTRADE
          </span>
          <span style={{ fontSize: 9, color: C.muted, letterSpacing: '0.18em', fontWeight: 500 }}>
            TREASURE COLLECTION
          </span>
        </div>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 11, color: C.muted, letterSpacing: '0.14em',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          ← BACK TO COLLECTION
        </button>
      </header>

      <div style={{
        maxWidth: 1200, margin: '0 auto',
        padding: '64px 48px',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80,
      }}>

        {/* ── LEFT: Image gallery ── */}
        <div>
          {/* Main image */}
          <div style={{ aspectRatio: '1/1', background: stone.bg, overflow: 'hidden', marginBottom: 16 }}>
            <img
              src={imageSrc}
              alt={stone.name}
              style={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                filter: hasAdditionalImages ? 'none' : imgFilter(activeImg),
                transform: hasAdditionalImages ? 'none' : `rotate(${fallbackTileAngles[activeImg]}deg) scale(1.04)`,
              }}
            />
          </div>

          {/* Thumbnails */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))', gap: 8 }}>
            {gallery.map((src, i) => (
              <div
                key={i}
                onClick={() => setActiveImg(i)}
                style={{
                  aspectRatio: '1/1', background: stone.bg, overflow: 'hidden',
                  cursor: 'pointer',
                  outline: activeImg === i ? `2px solid ${C.gold}` : '2px solid transparent',
                  outlineOffset: 2,
                  opacity: activeImg === i ? 1 : 0.65,
                  transition: 'all 0.15s',
                }}
              >
                <img
                  src={src}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', filter: hasAdditionalImages ? 'none' : thumbFilter(i), transform: hasAdditionalImages ? 'none' : `rotate(${fallbackTileAngles[i]}deg) scale(1.08)` }}
                />
              </div>
            ))}
          </div>

          {/* Catalog note */}
          <div style={{
            marginTop: 24, padding: '16px 20px',
            background: '#fff', border: `1px solid ${C.border}`,
            display: 'flex', gap: 14, alignItems: 'flex-start',
          }}>
            <div>
              <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', color: C.ink, marginBottom: 4 }}>
                CATALOG RECORD
              </p>
              <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                This item is presented from the Kingstrade treasure archive with its catalog image and valuation record.
              </p>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Info & CTA ── */}
        <div style={{ paddingTop: 8 }}>
          <p style={{ fontSize: 9, color: C.gold, letterSpacing: '0.24em', fontWeight: 600, marginBottom: 16 }}>
            {stone.author || 'UNKNOWN AUTHOR'} · {stone.id}
          </p>
          <h1 style={{
            fontFamily: 'Playfair Display', fontSize: 44, fontWeight: 400,
            color: C.ink, lineHeight: 1.1, marginBottom: 20,
          }}>
            {stone.name}
          </h1>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 32 }}>
            <span style={{ fontFamily: 'DM Mono', fontSize: 28, fontWeight: 600, color: C.ink }}>
              ${stone.price.toLocaleString()}
            </span>
            <StatusBadge status={stone.status} />
          </div>

          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.85, marginBottom: 36, fontWeight: 300, maxWidth: 440 }}>
            A cataloged treasure from the Kingstrade collection. This record includes the creator or institution,
            current valuation, and source image reference for review before reservation.
          </p>

          {/* Specs table */}
          <div style={{ marginBottom: 40 }}>
            {specs.map(({ label, value }, i) => (
              <div key={label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0',
                borderBottom: `1px solid ${C.border}`,
                borderTop: i === 0 ? `1px solid ${C.border}` : 'none',
              }}>
                <span style={{ fontSize: 11, color: C.muted, letterSpacing: '0.06em' }}>{label}</span>
                <span style={{
                  fontSize: 12, color: C.ink, fontWeight: 500,
                  fontFamily: (label === 'Treasure ID' || label === 'Valuation') ? 'DM Mono' : 'DM Sans',
                  textAlign: 'right',
                }}>
                  {value}
                </span>
              </div>
            ))}
          </div>

          {/* CTA */}
          {stone.status === 'AVAILABLE' ? (
            <>
              <button onClick={onReserve} style={{
                width: '100%', padding: '18px',
                background: C.gold, border: 'none', cursor: 'pointer',
                fontSize: 12, letterSpacing: '0.22em', fontWeight: 700, color: C.dark,
                marginBottom: 12,
                transition: 'opacity 0.2s',
              }}>
                RESERVE THIS TREASURE →
              </button>
              <p style={{ fontSize: 10, color: C.muted, textAlign: 'center', lineHeight: 1.6 }}>
                No account required · Secure reservation · Collection team follow-up
              </p>
            </>
          ) : (
            <div style={{ padding: '20px', background: '#f5f5f5', border: `1px solid ${C.border}`, textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: C.muted, letterSpacing: '0.1em' }}>
                THIS TREASURE HAS BEEN SOLD
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
